import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalculatorInputs } from '../components/CalculatorInputs';
import { AssumptionsSidebar } from '../components/AssumptionsSidebar';
import { ResultsSummary } from '../components/ResultsSummary';
import { ProfitChart } from '../components/ProfitChart';
import { ProjectionTable } from '../components/ProjectionTable';
import { HiringFunnelModal } from '../components/HiringFunnelModal';
import { ExpandableHiringFunnel } from '../components/ExpandableHiringFunnel';
import { Inputs, DEFAULT_INPUTS, calculateModel, PackageType, BuildPayType, IndustryType, optimizeCampaignsForRevenueTarget } from '../utils/roiCalculations';
import { fmtMoney } from '../utils/formatters';
const CORRECT_PASSWORD = 'HIRE100k';
const ADDITIONAL_PASSWORDS = ['mymanny', '1234', 'temp1234'];
const TEMP_PASSWORD = 'UNTD.solar';
const TEMP_PASSWORD_START = new Date('2026-02-02T00:00:00');
const TEMP_PASSWORD_END = new Date('2026-02-07T00:00:00'); // End of Feb 6th (midnight Feb 7th)
const TEMP_PASSWORD_2 = 'Polaris';
const TEMP_PASSWORD_2_START = new Date('2026-03-27T00:00:00');
const TEMP_PASSWORD_2_END = new Date('2026-03-30T00:00:00'); // End of Mar 29th (midnight Mar 30th)
const TEMP_PASSWORD_3 = '1234';
const TEMP_PASSWORD_3_START = new Date('2026-04-07T00:00:00');
const TEMP_PASSWORD_3_END = new Date('2026-04-23T00:00:00'); // End of Apr 22nd (midnight Apr 23rd)
const AUTH_KEY = 'hire_calculator_authenticated';
const isValidPassword = (password: string): boolean => {
  // Check permanent passwords
  if (password === CORRECT_PASSWORD) {
    return true;
  }
  // Check additional permanent passwords
  if (ADDITIONAL_PASSWORDS.includes(password)) {
    return true;
  }
  // Check temporary passwords within date range
  const now = new Date();
  if (password === TEMP_PASSWORD && now >= TEMP_PASSWORD_START && now < TEMP_PASSWORD_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_2 && now >= TEMP_PASSWORD_2_START && now < TEMP_PASSWORD_2_END) {
    return true;
  }
  if (password === TEMP_PASSWORD_3 && now >= TEMP_PASSWORD_3_START && now < TEMP_PASSWORD_3_END) {
    return true;
  }
  return false;
};
export function ROICalculator() {
  // Set browser tab title
  useEffect(() => {
    document.title = 'Recruiting ROI Calculator';
  }, []);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // Shared state for toggles
  const [halfSales, setHalfSales] = useState(false);
  const [doubleAttrition, setDoubleAttrition] = useState(false);
  const [onTarget, setOnTarget] = useState(false);
  const [preOnTargetValues, setPreOnTargetValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [plusFive, setPlusFive] = useState(false);
  const [prePlusFiveValues, setPrePlusFiveValues] = useState<{
    pctLaunched: number;
    pct30: number;
    pct90: number;
    pct1y: number;
    salesFirst90Total: number;
    salesVetMonthly: number;
  } | null>(null);
  const [packageScenario, setPackageScenario] = useState<'doubleAttrition' | 'plusFive' | null>(null);
  const previousPackageRef = useRef<PackageType | null>(null);
  const [preEdgeAdSpend, setPreEdgeAdSpend] = useState<{
    adSpend: number;
    monthlyAdSpend?: number[];
  } | null>(null);
  const [preOptimizeMonthlyCampaigns, setPreOptimizeMonthlyCampaigns] = useState<number[] | null>(null);
  const [timeframe, setTimeframe] = useState<3 | 6 | 12>(6);
  const [savePdfLabel, setSavePdfLabel] = useState('Save as PDF');
  const [showPackageTooltip, setShowPackageTooltip] = useState(false);
  const [showBuildTooltip, setShowBuildTooltip] = useState(false);
  const [expandedReviewSections, setExpandedReviewSections] = useState({
    campaignAdSpend: false,
    revenueMargins: false,
    salesPerformance: false,
    retentionModeling: false
  });
  const [showHiringFunnel, setShowHiringFunnel] = useState(false);
  const [showHiringMetrics, setShowHiringMetrics] = useState(false);
  const [showOverhead, setShowOverhead] = useState(false);
  // Overhead values state
  const [preInterviewHours, setPreInterviewHours] = useState(40);
  const [interviewToOnboardingHours, setInterviewToOnboardingHours] = useState(60);
  const [adminCost, setAdminCost] = useState(2000);
  const [leadershipCost, setLeadershipCost] = useState(3000);
  const resultsRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  // Check authentication on mount (client-side only)
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem(AUTH_KEY);
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Load state from URL on mount
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams(window.location.search);
    if ([...params.keys()].length === 0) return;
    const newInputs = {
      ...DEFAULT_INPUTS
    };
    const parseNum = (key: string, def: number) => {
      const val = params.get(key);
      return val ? parseFloat(val) : def;
    };
    if (params.has('pkg')) newInputs.pkg = params.get('pkg') as PackageType;
    if (params.has('buildPay')) newInputs.buildPay = params.get('buildPay') as BuildPayType;
    if (params.has('industry')) newInputs.industry = params.get('industry') as IndustryType;
    if (params.has('salesSchoolEnabled')) {
      newInputs.salesSchoolEnabled = params.get('salesSchoolEnabled') !== 'false';
    }
    newInputs.campaigns = parseNum('campaigns', DEFAULT_INPUTS.campaigns);
    newInputs.adSpend = parseNum('adSpend', DEFAULT_INPUTS.adSpend);
    newInputs.cph = parseNum('cph', DEFAULT_INPUTS.cph);
    newInputs.ticket = parseNum('ticket', DEFAULT_INPUTS.ticket);
    newInputs.gpm = parseNum('gpm', DEFAULT_INPUTS.gpm);
    newInputs.salesVetMonthly = parseNum('salesVetMonthly', DEFAULT_INPUTS.salesVetMonthly);
    newInputs.salesFirst90Total = parseNum('salesFirst90Total', DEFAULT_INPUTS.salesFirst90Total);
    newInputs.pctLaunched = parseNum('pctLaunched', DEFAULT_INPUTS.pctLaunched);
    newInputs.pct30 = parseNum('pct30', DEFAULT_INPUTS.pct30);
    newInputs.pct90 = parseNum('pct90', DEFAULT_INPUTS.pct90);
    newInputs.pct1y = parseNum('pct1y', DEFAULT_INPUTS.pct1y);
    newInputs.pullthrough = parseNum('pullthrough', DEFAULT_INPUTS.pullthrough);
    // Initialize monthly campaigns based on base campaigns if not present
    newInputs.monthlyCampaigns = Array(12).fill(newInputs.campaigns);
    newInputs.monthlyAdSpend = Array(12).fill(newInputs.adSpend);
    setInputs(newInputs);
    if (params.has('calculated') && params.get('calculated') === 'true') {
      setHasCalculated(true);
    }
  }, [isClient]);
  // Update URL when inputs change
  useEffect(() => {
    if (!isClient) return;
    const params = new URLSearchParams();
    Object.entries(inputs).forEach(([key, value]) => {
      if (key !== 'monthlyCampaigns' && key !== 'monthlyAdSpend') {
        // Don't clutter URL with arrays
        params.set(key, String(value));
      }
    });
    if (hasCalculated) {
      params.set('calculated', 'true');
    }
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [inputs, hasCalculated, isClient]);
  // Sync monthly campaigns when base campaigns change - ONE WAY ONLY
  useEffect(() => {
    // When base campaigns changes, ALWAYS update all monthly campaigns to match
    // This is a one-way sync: top → table only
    if (inputs.campaigns !== undefined) {
      // Only update if monthlyCampaigns doesn't exist or if all values match the old campaigns value
      // This allows manual edits in the table to persist
      const needsInit = !inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyCampaigns: Array(12).fill(inputs.campaigns)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyCampaigns.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current campaigns input,
        // update them all to match the new campaigns value
        if (allSame && inputs.monthlyCampaigns[0] !== inputs.campaigns) {
          setInputs(prev => ({
            ...prev,
            monthlyCampaigns: Array(12).fill(inputs.campaigns)
          }));
        }
      }
    }
  }, [inputs.campaigns]);
  // Sync monthly ad spend when base ad spend changes - ONE WAY ONLY
  useEffect(() => {
    if (inputs.adSpend !== undefined) {
      const needsInit = !inputs.monthlyAdSpend || inputs.monthlyAdSpend.length !== 12;
      if (needsInit) {
        setInputs(prev => ({
          ...prev,
          monthlyAdSpend: Array(12).fill(inputs.adSpend)
        }));
      } else {
        // Check if all monthly values are the same (no manual edits)
        const allSame = inputs.monthlyAdSpend.every((c, i, arr) => c === arr[0]);
        // If all values are the same AND different from current ad spend input,
        // update them all to match the new ad spend value
        if (allSame && inputs.monthlyAdSpend[0] !== inputs.adSpend) {
          setInputs(prev => ({
            ...prev,
            monthlyAdSpend: Array(12).fill(inputs.adSpend)
          }));
        }
      }
    }
  }, [inputs.adSpend]);
  // Initialize if missing
  if (!inputs.monthlyCampaigns || inputs.monthlyCampaigns.length !== 12) {
    setInputs(prev => ({
      ...prev,
      monthlyCampaigns: Array(12).fill(prev.campaigns)
    }));
  }
  // Calculate results - recalculates whenever inputs change (including monthlyCampaigns and monthlyAdSpend)
  const results = useMemo(() => {
    // Set default buildPay if not selected
    const inputsWithDefaults = {
      ...inputs,
      pkg: inputs.pkg,
      buildPay: inputs.buildPay || 'upfront'
    };
    return calculateModel(inputsWithDefaults);
  }, [inputs, inputs.monthlyCampaigns, inputs.monthlyAdSpend, inputs.pkg, inputs.buildPay]);
  const toggleReviewSection = (section: keyof typeof expandedReviewSections) => {
    setExpandedReviewSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidPassword(password)) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, 'true');
      setError('');
    } else {
      setError('Incorrect access code. Please try again.');
      setPassword('');
    }
  };
  const handleCalculate = () => {
    // Ensure monthly campaigns are initialized
    if (!inputs.monthlyCampaigns) {
      setInputs(prev => ({
        ...prev,
        monthlyCampaigns: Array(12).fill(prev.campaigns)
      }));
    }
    setHasCalculated(true);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReview = () => {
    setHasReviewed(true);
    setTimeout(() => {
      reviewRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    setHasCalculated(false);
    setHasReviewed(false);
    setHalfSales(false);
    setDoubleAttrition(false);
    setOnTarget(false);
    setPreOnTargetValues(null);
    setPlusFive(false);
    setPrePlusFiveValues(null);
    setPackageScenario(null);
    setPreEdgeAdSpend(null);
    previousPackageRef.current = DEFAULT_INPUTS.pkg;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Package-recommended scenarios are removed when leaving their package.
  // Manual scenario changes clear this ownership marker and remain untouched.
  useEffect(() => {
    const previousPackage = previousPackageRef.current;
    if (previousPackage === inputs.pkg) return;
    if (previousPackage === 'edge' && inputs.pkg !== 'edge' && preEdgeAdSpend) {
      setInputs(prev => ({
        ...prev,
        ...preEdgeAdSpend
      }));
      setPreEdgeAdSpend(null);
    }
    if (inputs.pkg === 'edge' && previousPackage !== 'edge') {
      setPreEdgeAdSpend({
        adSpend: inputs.adSpend,
        monthlyAdSpend: inputs.monthlyAdSpend ? [...inputs.monthlyAdSpend] : undefined
      });
      setInputs(prev => ({
        ...prev,
        adSpend: 2500,
        monthlyAdSpend: Array(12).fill(2500)
      }));
    }
    if (packageScenario === 'doubleAttrition') {
      setDoubleAttrition(false);
    }
    if (packageScenario === 'plusFive') {
      if (prePlusFiveValues) {
        setInputs(prev => ({
          ...prev,
          ...prePlusFiveValues
        }));
      }
      setPlusFive(false);
      setPrePlusFiveValues(null);
    }
    setPackageScenario(null);
    if (inputs.pkg === 'edge' && !doubleAttrition) {
      setDoubleAttrition(true);
      setPackageScenario('doubleAttrition');
    }
    if (inputs.pkg === 'summit' && !plusFive) {
      setPrePlusFiveValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      setInputs(prev => ({
        ...prev,
        pctLaunched: Math.min(100, (prev.pctLaunched as number) + 5),
        pct30: Math.min(100, (prev.pct30 as number) + 5),
        pct90: Math.min(100, (prev.pct90 as number) + 5),
        pct1y: Math.min(100, (prev.pct1y as number) + 5),
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.05 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.05 * 10) / 10
      }));
      setPlusFive(true);
      setPackageScenario('plusFive');
    }
    previousPackageRef.current = inputs.pkg;
  }, [inputs.pkg]);
  const handleDoubleAttritionChange = (value: boolean) => {
    setPackageScenario(scenario => scenario === 'doubleAttrition' ? null : scenario);
    setDoubleAttrition(value);
  };
  const handleToggleOnTarget = () => {
    if (!onTarget) {
      // Save current values before overriding
      setPreOnTargetValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      // Apply "On Target" values
      setInputs(prev => ({
        ...prev,
        pctLaunched: 80,
        pct30: 40,
        pct90: 80,
        pct1y: 40,
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.1 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.1 * 10) / 10
      }));
      setOnTarget(true);
    } else {
      // Restore saved values
      if (preOnTargetValues) {
        setInputs(prev => ({
          ...prev,
          ...preOnTargetValues
        }));
      }
      setOnTarget(false);
      setPreOnTargetValues(null);
    }
  };
  const handlePlusFive = () => {
    setPackageScenario(scenario => scenario === 'plusFive' ? null : scenario);
    if (!plusFive) {
      // Save current values before applying +5%
      setPrePlusFiveValues({
        pctLaunched: inputs.pctLaunched as number,
        pct30: inputs.pct30 as number,
        pct90: inputs.pct90 as number,
        pct1y: inputs.pct1y as number,
        salesFirst90Total: inputs.salesFirst90Total as number,
        salesVetMonthly: inputs.salesVetMonthly as number
      });
      setInputs(prev => ({
        ...prev,
        pctLaunched: Math.min(100, (prev.pctLaunched as number) + 5),
        pct30: Math.min(100, (prev.pct30 as number) + 5),
        pct90: Math.min(100, (prev.pct90 as number) + 5),
        pct1y: Math.min(100, (prev.pct1y as number) + 5),
        salesFirst90Total: Math.round((prev.salesFirst90Total as number) * 1.05 * 10) / 10,
        salesVetMonthly: Math.round((prev.salesVetMonthly as number) * 1.05 * 10) / 10
      }));
      setPlusFive(true);
    } else {
      // Restore saved values
      if (prePlusFiveValues) {
        setInputs(prev => ({
          ...prev,
          ...prePlusFiveValues
        }));
      }
      setPlusFive(false);
      setPrePlusFiveValues(null);
    }
  };
  const handleOptimizeForTarget = (target: number, months: 3 | 6 | 12 = timeframe) => {
    if (!target || target <= 0) return;
    const stash = inputs.monthlyCampaigns && inputs.monthlyCampaigns.length === 12 ? [...inputs.monthlyCampaigns] : Array(12).fill(inputs.campaigns);
    const pattern = optimizeCampaignsForRevenueTarget(inputs, target, months);
    if (pattern) {
      setPreOptimizeMonthlyCampaigns(stash);
      setInputs({
        ...inputs,
        monthlyCampaigns: pattern,
        campaigns: pattern[0],
        revenueTarget: target
      });
    }
  };
  const handleUndoOptimize = () => {
    if (preOptimizeMonthlyCampaigns) {
      setInputs({
        ...inputs,
        monthlyCampaigns: preOptimizeMonthlyCampaigns,
        campaigns: preOptimizeMonthlyCampaigns[0]
      });
      setPreOptimizeMonthlyCampaigns(null);
    }
  };
  // Handle numeric input changes for Review section
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyLinkLabel('Link Copied✓');
      setTimeout(() => setCopyLinkLabel('Copy Share Link'), 1200);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };
  const handleSavePdf = async () => {
    setSavePdfLabel('Generating PDF...');
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      const element = resultsRef.current;
      if (!element) return;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      const companyName = inputs.companyName || 'Company';
      const fileName = `${companyName.replace(/\s+/g, '_')}_ROI_Projection.pdf`;
      pdf.save(fileName);
      setSavePdfLabel('PDF Saved ✓');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    } catch (err) {
      console.error('Failed to generate PDF', err);
      setSavePdfLabel('Failed - Try Again');
      setTimeout(() => setSavePdfLabel('Save as PDF'), 2000);
    }
  };
  // Calculated values - REVERSED RETENTION FUNNEL
  // Start with 1 rep at Active 1 Year and calculate upward
  const targetActive1Year = 1; // Fixed: 1 rep who stays long-term
  // Work backwards through retention rates to find how many hires needed
  const reverseActive90 = targetActive1Year / (inputs.pct1y / 100);
  const reverseActive30 = reverseActive90 / (inputs.pct90 / 100);
  const reverseLaunched = reverseActive30 / (inputs.pct30 / 100);
  const reverseHires = reverseLaunched / (inputs.pctLaunched / 100);
  // Keep the original forward calculations for other uses
  const adSpendTotal = inputs.adSpend * inputs.campaigns;
  const estimatedHires = Math.round(adSpendTotal / inputs.cph);
  const estimatedLaunched = estimatedHires * (inputs.pctLaunched / 100);
  const estimatedActive30 = estimatedLaunched * (inputs.pct30 / 100);
  const estimatedActive90 = estimatedActive30 * (inputs.pct90 / 100);
  const estimatedActive1Year = estimatedActive90 * (inputs.pct1y / 100);
  const gpPerSale = inputs.ticket * (inputs.gpm / 100);
  const rookieTotalSales = inputs.salesFirst90Total;
  // Round rep counts to nearest tenth for clean display
  const roundedActive90 = Math.round(estimatedActive90 * 10) / 10;
  const roundedActive1Year = Math.round(estimatedActive1Year * 10) / 10;
  const rookieMonthlyRevenue = roundedActive90 * rookieTotalSales * inputs.ticket * (inputs.pullthrough / 100) / 3;
  const veteranMonthlyRevenue = roundedActive1Year * inputs.salesVetMonthly * inputs.ticket * (inputs.pullthrough / 100);
  const canCalculate = true; // Allow calculation even without package selected
  // Show loading during initial mount to prevent hydration mismatch
  if (!isClient) {
    return <div className="d2d-theme min-h-screen" data-id="element-512" data-mp="3rqu0atw5tk0i"></div>;
  }
  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <div className="d2d-theme min-h-screen flex items-center justify-center p-4" data-id="element-513" data-mp="37no8eodgtcod">
        <div className="w-full max-w-md" data-id="element-514" data-mp="33ndyx1qgs44r">
          <div className="text-center mb-8" data-id="element-515" data-mp="2yw5mrxxr24nw">
            <img src="https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png" alt="HIRE" className="h-12 mx-auto mb-6" data-id="element-516" data-mp="2yuukk2jyd4yw" />
            <h1 className="text-2xl font-bold text-navy mb-2" data-id="element-517" data-mp="2oyrdcia8ug53">
              ROI Calculator Access
            </h1>
            <p className="text-gray-600" data-id="element-518" data-mp="2jxk2gact10u7">Enter your access code to continue</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white border-2 border-gray-200 rounded-lg p-8 shadow-lg" data-id="element-519" data-mp="2fxjp6t1fyvoq">
            <div className="mb-6" data-id="element-520" data-mp="e6gcly7zr71j">
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-521" data-mp="e4lsun8ghkz1">
                Access Code
              </label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 rounded border-2 border-gray-200 bg-white text-gray-900 outline-none focus:border-amber transition-colors font-mono" placeholder="Enter code" autoFocus data-id="element-522" data-mp="gpjr82exl34a" />
              {error && <p className="mt-2 text-sm text-red-600" data-id="element-523" data-mp="3tyzanhpmj43y">{error}</p>}
            </div>

            <button type="submit" className="w-full py-3 rounded bg-amber hover:bg-amber-dark text-navy font-bold text-base transition-colors shadow-md" data-id="element-524" data-mp="2r3he2lruwhx">
              Access Calculator
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6" data-id="element-525" data-mp="1j5l8vwwc4c6r">
            Your access will be saved for future visits
          </p>
        </div>
      </div>;
  }
  // Main calculator content
  return <div className="d2d-theme min-h-screen" data-id="element-526" data-mp="1lqaxht1zh551">
      {/* Floating Edit Button - Always visible */}
      <button onClick={() => setIsSidebarOpen(true)} className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-amber hover:bg-amber-dark text-navy shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95" title="Edit Assumptions" data-id="element-527" data-mp="1gr82z2jmqhbb">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-528" data-mp="24n2efvu1z85x">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-529" data-mp="2eg020gdwjnbe" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-530" data-mp="29fm1djemrfne" />
        </svg>
      </button>

      {/* Assumptions Sidebar */}
      <AssumptionsSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} inputs={inputs} onChange={setInputs} data-id="element-531" data-mp="2jd9wiag5ep63" />

      {/* Header */}
      <header className="border-b border-white/10 bg-navy/95" data-id="element-532" data-mp="370evlao0vpc2">
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-6" data-id="element-533" data-mp="36zvf7ye0t905">
          <div className="flex items-center gap-4 shrink-0" data-id="element-534" data-mp="2t0cis2uknm3o">
            <div className="rounded-md px-3 py-2 shadow-sm" style={{
            backgroundColor: '#f1fbf8'
          }} data-id="element-535" data-mp="2xyxfvtej1pnw">
              <img src="https://cdn.magicpatterns.com/uploads/agUMR24bt7hzCEPWvymd5Y/Full_Multi_color.png" alt="HIRE" className="h-7 md:h-8" data-id="element-536" data-mp="2j8py7fwcgao9" />
            </div>
            <span className="hidden sm:block pl-4 border-l border-white/15 text-[10px] font-bold tracking-[0.16em] text-amber uppercase" data-id="element-537" data-mp="2ebpsx77bjby7">
              High Impact Recruiting Engine
            </span>
          </div>
          <span className="hidden md:flex items-center gap-2 text-xs font-semibold text-slate-300" data-id="element-538" data-mp="25bkv9orxfrx8">
            <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-539" data-mp="avcqlgfyij6m" />
            Built for D2D teams
          </span>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-5 md:px-8 py-10 md:py-16 pb-20" data-id="element-540" data-mp="62jnsy41tkx6">
        {/* Hero Section */}
        <div className="mb-12 md:mb-16 text-center max-w-4xl mx-auto" data-id="element-541" data-mp="22rcidn9t0o4">
          <div className="inline-flex items-center gap-2 mb-5 text-[11px] font-extrabold tracking-[0.16em] uppercase text-amber" data-id="element-542" data-mp="3ta3rqn4lf41e">
            <span className="w-1.5 h-1.5 rounded-full bg-amber" data-id="element-543" data-mp="trkxspsj544k" />
            Recruiting forecast for D2D sales leaders
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white leading-[1.08] tracking-[-0.045em] mb-5" data-id="element-544" data-mp="ksqxopfzpvm9">
            Predictable recruiting, profitable growth.
          </h1>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto" data-id="element-545" data-mp="35iye8em4knu9">
            High impact recruiting engine.
          </p>
        </div>

        {/* Step 1: Input Form */}
        <div className="mb-8 md:mb-12" data-id="element-546" data-mp="2qgggv4qvbfh3">
          <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-547" data-mp="2qhj8xfzxfnyl">
            <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-548" data-mp="2verr3gl2rrzu">
              1
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-549" data-mp="30d2wnf4c7gc9">
              Business Assumptions
            </h2>
          </div>
          <CalculatorInputs inputs={inputs} onChange={setInputs} onReset={handleReset} onReview={handleReview} showActionButtons={false} data-id="element-550" data-mp="396y44tsxliv2" />
        </div>

        {/* Step 2: Validation Summary */}
        {hasReviewed && <div ref={reviewRef} className="mb-8 md:mb-12" data-id="element-551" data-mp="3o3ovqgwd5qa4">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-552" data-mp="3o372zu8wtu6y">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-553" data-mp="1ormc96nqovwg">
                2
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-554" data-mp="1trs8z18v98le">
                Review Your Numbers
              </h2>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg" data-id="element-555" data-mp="3qnd6y1xixkni">
              {/* Hiring Funnel Breakdown Button */}
              <div className="mb-6 flex justify-end gap-3" data-id="element-556" data-mp="99pvafh5zlkv">
                <button onClick={handlePlusFive} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${plusFive ? 'bg-blue-500 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'} active:translate-y-[1px]`} data-id="element-557" data-mp="4a4vy3y31b7m">
                  {plusFive ? '✓ ' : ''}+5%
                </button>
                <button onClick={handleToggleOnTarget} className={`px-3 py-2 rounded text-xs font-semibold transition-all border-2 ${onTarget ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300 hover:border-emerald-400'}`} data-id="element-558" data-mp="3hm5brsqb0zxn">
                  {onTarget ? '✓ ' : ''}On Target
                </button>
              </div>

              {/* Monthly Ad Investment & Retention Funnel - Side by Side */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" data-id="element-559" data-mp="uk45he4ryilu">
                {/* Left: Monthly Ad Investment */}
                <div data-id="element-560" data-mp="zjqj3ooim0v6"></div>

                {/* Right: Retention Funnel Visualization */}
              </div>

              {/* Expandable Hiring Funnel - Conditionally shown */}
              <div className="mb-4" data-id="element-561" data-mp="1s5tapij4dgm2">
                {/* Overhead Section - appears ABOVE Recruiting Metrics */}
                {showOverhead && <div className="mb-8" data-id="element-562" data-mp="1ifzwe9rlene4">
                    <div className="relative group" data-id="element-563" data-mp="1ieylq6eho2wx">
                      <h4 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-564" data-mp="265ub6wvh2m10">
                        Overhead
                        <button onClick={e => {
                    e.stopPropagation();
                    setShowOverhead(false);
                  }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Overhead" data-id="element-565" data-mp="2aynav6c33468">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-566" data-mp="2u0bs5y5byiis">
                            <polyline points="6 9 12 15 18 9" data-id="element-567" data-mp="o58zumyfbz3a" />
                          </svg>
                          Collapse
                        </button>
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-568" data-mp="23noswe7yx3kz">
                      {/* Total Time Investment */}
                      <div className="p-4 rounded bg-orange-50 border-2 border-orange-200" data-id="element-569" data-mp="1yw740oj7ytgd">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-570" data-mp="1txdqiw3xn2g7">
                          Total Time Investment
                        </div>
                        <div className="space-y-3" data-id="element-571" data-mp="1owzi3cyf5qhn">
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-572" data-mp="1kwxntov0jzce">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-573" data-mp="1fz5v4smjo975">
                              Pre-Interview
                            </div>
                            <div className="relative" data-id="element-574" data-mp="3abc9g0aqj63o">
                              <input type="number" min="0" step="1" value={preInterviewHours} onChange={e => setPreInterviewHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-575" data-mp="35dtvtwlrw2pc" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-576" data-mp="1rdh21i2qm0r3">
                                hours
                              </span>
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-orange-100 p-2 rounded transition-colors" data-id="element-577" data-mp="111n32i52sap">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-578" data-mp="60wb8yaj53s2">
                              Interview-to-Onboarding
                            </div>
                            <div className="relative" data-id="element-579" data-mp="2fa3yfyna7quv">
                              <input type="number" min="0" step="1" value={interviewToOnboardingHours} onChange={e => setInterviewToOnboardingHours(parseFloat(e.target.value) || 0)} className="w-full text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-580" data-mp="2fboobaynilme" />
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm" data-id="element-581" data-mp="1hsdlk0hbg23r">
                                hours
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Money Value of Time */}
                      <div className="p-4 rounded bg-red-50 border-2 border-red-200" data-id="element-582" data-mp="20fgq028vvl9v">
                        <div className="text-sm font-bold text-navy mb-3" data-id="element-583" data-mp="256yv92hxw80n">
                          Money Value of Time
                        </div>
                        <div className="space-y-3" data-id="element-584" data-mp="2a5a5vfg26mjx">
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-585" data-mp="2e6dizu6q24hg">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-586" data-mp="2j5yf7wuibyqh">
                              Admin
                            </div>
                            <div className="relative" data-id="element-587" data-mp="osqiu0qvvf2q">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-588" data-mp="ou1l1w4okerq">
                                $
                              </span>
                              <input type="text" value={adminCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setAdminCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-589" data-mp="m93mogy7gwmh" />
                            </div>
                          </div>
                          <div className="cursor-pointer hover:bg-red-100 p-2 rounded transition-colors" data-id="element-590" data-mp="3fyy5sv8t30p4">
                            <div className="text-xs text-gray-600 mb-1 font-semibold uppercase tracking-wide" data-id="element-591" data-mp="3ayjwl9vgxkpi">
                              Leadership
                            </div>
                            <div className="relative" data-id="element-592" data-mp="27214nj4f9iyp">
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-lg" data-id="element-593" data-mp="26zn815u3fyp1">
                                $
                              </span>
                              <input type="text" value={leadershipCost.toLocaleString()} onChange={e => {
                          const numericValue = e.target.value.replace(/,/g, '');
                          setLeadershipCost(parseFloat(numericValue) || 0);
                        }} className="w-full pl-5 text-xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-594" data-mp="1y35yu85aiavx" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                <div className="relative group" data-id="element-595" data-mp="qoxc8bbblzi9">
                  {/* Hidden arrow above Recruiting Metrics heading - shows when funnel is fully expanded */}
                  {showHiringMetrics && !showOverhead && <button onClick={() => setShowOverhead(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Overhead" data-id="element-596" data-mp="10nmkdcdf05lb">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-597" data-mp="1jecjqr3s1a9f">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-598" data-mp="1jc9ptvzi6wzf">
                          <polyline points="18 15 12 9 6 15" data-id="element-599" data-mp="3hhzj6t9dr9d8" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-600" data-mp="3mgj6oy3mgt7p">
                        Click to expand
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-601" data-mp="1rglcu9germ">
                    Recruiting Metrics
                    {/* Collapse button for both Overhead + Recruiting Metrics */}
                    {showHiringMetrics && <button onClick={e => {
                  e.stopPropagation();
                  setShowHiringMetrics(false);
                  setShowOverhead(false);
                }} className="ml-3 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-xs font-bold text-gray-400 hover:text-amber-500 uppercase tracking-wide align-middle" aria-label="Collapse Recruiting Metrics" data-id="element-602" data-mp="1ktdsu9nh1ja3">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-603" data-mp="t393dwkuu620">
                          <polyline points="6 9 12 15 18 9" data-id="element-604" data-mp="3gxr59wioqiqe" />
                        </svg>
                        Collapse
                      </button>}
                  </h3>
                </div>

                {showHiringMetrics && <div className="mb-6" data-id="element-605" data-mp="378haua0dcnz4">
                    <ExpandableHiringFunnel estimatedHires={reverseHires} data-id="element-606" data-mp="329einq5hmc8k" />
                  </div>}
              </div>

              {/* Projected Recruiting Metrics */}
              <div className="mb-8" data-id="element-607" data-mp="3uxsga4snz6jl">
                <div className="relative group" data-id="element-608" data-mp="3uytuuj8w2b62">
                  {/* Hidden arrow above Retention Metrics heading */}
                  {!showHiringMetrics && <button onClick={() => setShowHiringMetrics(true)} className="absolute -top-6 left-0 w-full flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10" aria-label="Show Recruiting Metrics" data-id="element-609" data-mp="3lxdtt23yrlei">
                      <div className="w-20 text-right flex justify-end pr-4" data-id="element-610" data-mp="q1hciyumsjf5">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-bounce" data-id="element-611" data-mp="u2jdbk4753j8">
                          <polyline points="18 15 12 9 6 15" data-id="element-612" data-mp="1drbn19rh9vw8" />
                        </svg>
                      </div>
                      <div className="text-xs font-bold text-amber-500 uppercase tracking-wide" data-id="element-613" data-mp="1mpx38y9h8ytu">
                        Recruiting Metrics
                      </div>
                    </button>}

                  <h3 className="text-lg font-bold text-navy mb-4 border-b border-gray-200 pb-2" data-id="element-614" data-mp="3oh0gzwenytim">
                    Retention Metrics
                  </h3>
                </div>

                <div className="space-y-3" data-id="element-615" data-mp="qo8fgd4hnu12">
                  <div className="flex items-center gap-3" data-id="element-616" data-mp="qpa1fcn1m0xk">
                    <div className="w-20 text-right" data-id="element-617" data-mp="14nhtb6582c1v">
                      <span className="text-xl font-bold text-navy" data-id="element-618" data-mp="14p1f1c9ndfb1">
                        {reverseHires.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-619" data-mp="1ekw2qlsqa0ko">
                      Hired
                    </div>
                    <div className="flex-1 bg-white/10 rounded-full h-8 relative overflow-hidden border border-white/10" data-id="element-620" data-mp="19kqezgpkwy7v">
                      <div className="absolute inset-y-0 left-0 bg-amber rounded-full" style={{
                    width: '100%'
                  }} data-id="element-621" data-mp="1jcmij7afpgjh" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-622" data-mp="1sbhmecq0aejm">
                    <div className="w-20 text-right" data-id="element-623" data-mp="2c0fe38pb3pr2">
                      <span className="text-lg font-bold text-navy" data-id="element-624" data-mp="2gxxfme0zv2p1">
                        {reverseLaunched.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-625" data-mp="223aqd4ftbm85">
                      Launched
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-626" data-mp="1t836a6fvvw9j">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber to-amber/90 rounded-full" style={{
                    width: `${reverseLaunched / reverseHires * 100}%`
                  }} data-id="element-627" data-mp="1xzl44lmm1gqa" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-628" data-mp="1aaabxsjbsunr">
                      {inputs.pctLaunched}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-629" data-mp="178fbcn4stvd1">
                    <div className="w-20 text-right" data-id="element-630" data-mp="17a03y77jz489">
                      <span className="text-lg font-bold text-navy" data-id="element-631" data-mp="12adrhzzjts0i">
                        {reverseActive30.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-632" data-mp="sk19aef7iaxj">
                      Active 30d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-633" data-mp="oiz83s1qbosx">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full" style={{
                    width: `${reverseActive30 / reverseHires * 100}%`
                  }} data-id="element-634" data-mp="jh9tk2fast03" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-635" data-mp="23yeqx1jd9u54">
                      {inputs.pct30}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-636" data-mp="2cf6rxmrbio3z">
                    <div className="w-20 text-right" data-id="element-637" data-mp="2gg8r63l8j05y">
                      <span className="text-lg font-bold text-navy" data-id="element-638" data-mp="2genw8cv0df7k">
                        {reverseActive90.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-639" data-mp="2q6br6nqrxeds">
                      Active 90d
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-640" data-mp="1xhwhmgqevo9u">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full" style={{
                    width: `${reverseActive90 / reverseHires * 100}%`
                  }} data-id="element-641" data-mp="22hiugp2bv2i4" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-642" data-mp="3iug49uboctqi">
                      {inputs.pct90}%
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-643" data-mp="2o8iu3v1ibd4u">
                    <div className="w-20 text-right" data-id="element-644" data-mp="2o80ssjzzm8q2">
                      <span className="text-lg font-bold text-navy" data-id="element-645" data-mp="335t4f12eycyz">
                        {targetActive1Year.toFixed(1)}
                      </span>
                    </div>
                    <div className="w-28 text-base font-semibold text-gray-700" data-id="element-646" data-mp="2agm6o4as9res">
                      Active 1 Year
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden" data-id="element-647" data-mp="25j3tg1pqgq0z">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-400 rounded-full" style={{
                    width: `${targetActive1Year / reverseHires * 100}%`
                  }} data-id="element-648" data-mp="2k7gu3bzrmx1t" />
                    </div>
                    <div className="w-16 text-sm font-semibold text-gray-600" data-id="element-649" data-mp="1mpgpnlt3wt2w">
                      {inputs.pct1y}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Performance and Deal Economics - Side by Side */}
              <div className="mb-8" data-id="element-650" data-mp="3oia32fbj2v2k">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-651" data-mp="3tfa9wsg7c1uq">
                  {/* Left Column: Monthly Ad Spent */}
                  <div data-id="element-652" data-mp="l0rweb7hru3q">
                    <div className="p-4 md:p-6 rounded bg-gray-50 border-2 border-gray-200 flex flex-col justify-center cursor-pointer hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-653" data-mp="l0jkalj70stl">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-654" data-mp="g2i3665bv3kr">
                        Monthly Ad Spend
                      </div>
                      <div className="relative" data-id="element-655" data-mp="30sfrs26uxekf">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-656" data-mp="3se7fttfzth1f">
                          $
                        </span>
                        <input type="text" value={adSpendTotal.toLocaleString()} onChange={e => {
                      const numericValue = e.target.value.replace(/,/g, '');
                      const newTotal = parseFloat(numericValue) || 0;
                      const newAdSpend = inputs.campaigns > 0 ? newTotal / inputs.campaigns : newTotal;
                      handleNumericChange('adSpend', newAdSpend.toString());
                    }} className="w-full pl-6 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-657" data-mp="3nhpfd5rp44uw" />
                      </div>
                    </div>
                  </div>

                  {/* Middle Column: Ad Cost per Hire */}
                  <div data-id="element-658" data-mp="1f8j043upoxg5">
                    <div className="p-4 md:p-6 rounded bg-blue-50 border-2 border-blue-200 flex flex-col justify-center cursor-pointer hover:border-amber transition-colors" style={{
                  minHeight: '120px'
                }} data-id="element-659" data-mp="1f92fbcsz97qh">
                      <div className="text-xs text-gray-600 mb-2 font-semibold uppercase tracking-wide" data-id="element-660" data-mp="1tz02bpf67re3">
                        Ad Cost per Hire
                      </div>
                      <div className="relative" data-id="element-661" data-mp="22z57ru0t0fpm">
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl" data-id="element-662" data-mp="27vvnuhclzbb1">
                          $
                        </span>
                        <input type="number" min="1" step="10" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full pl-6 text-2xl md:text-3xl font-bold text-navy bg-transparent border-none outline-none focus:text-amber transition-colors" data-id="element-663" data-mp="3en5y3evmw8n5" />
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Expected Monthly Hires */}
                  <div data-id="element-664" data-mp="3ojr3w2fh9ns1">
                    <div className="p-4 md:p-6 rounded-lg bg-amber-light border border-amber flex flex-col justify-center" style={{
                  minHeight: '120px'
                }} data-id="element-665" data-mp="3oj8xwe30jv6x">
                      <div className="text-xs text-amber mb-2 font-extrabold uppercase tracking-wide" data-id="element-666" data-mp="25lcoowbjnaf5">
                        Expected Monthly Hires
                      </div>
                      <div className="text-3xl md:text-4xl font-extrabold text-white" data-id="element-667" data-mp="2ef7l9fylnsj6">
                        {Math.round(adSpendTotal / inputs.cph)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button onClick={handleCalculate} className="w-full py-4 md:py-5 rounded font-bold text-base md:text-lg transition-all shadow-lg bg-amber hover:bg-amber-dark text-navy active:translate-y-[1px] cursor-pointer" data-id="element-668" data-mp="1hooue3ow03m1">
                Calculate My ROI →
              </button>
            </div>
          </div>}

        {/* Step 3: Results */}
        {hasCalculated && results && <div ref={resultsRef} className="scroll-mt-8" data-id="element-669" data-mp="323ihg5tcp414">
            <div className="flex items-center gap-3 mb-4 md:mb-6" data-id="element-670" data-mp="1zr4bqgvh1so1">
              <div className="w-10 h-10 rounded bg-amber flex items-center justify-center text-lg font-bold text-navy" data-id="element-671" data-mp="1zqumwwm5xrjx">
                3
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-672" data-mp="28rkefo4e4vru">
                Projected Investment and ROI
              </h2>
            </div>

            <div className="mb-4" data-id="element-673" data-mp="2dostvgy5mlpe">
              {inputs.pkg && <div className="inline-block bg-amber-light text-amber-dark px-4 py-2 rounded text-sm font-semibold border-2 border-amber/20" data-id="element-674" data-mp="1h1xs8rmusx16">
                  {results.preset.name}
                </div>}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 md:p-7 shadow-lg mb-6" data-id="element-675" data-mp="1pzhvg64a13ef">
              <ResultsSummary results={results} halfSales={halfSales} doubleAttrition={doubleAttrition} timeframe={timeframe} onOptimizeForTarget={handleOptimizeForTarget} onUndoOptimize={handleUndoOptimize} canUndoOptimize={preOptimizeMonthlyCampaigns !== null} data-id="element-676" data-mp="1utvpa43aj2gz" />
            </div>

            <div className="mb-6" data-id="element-677" data-mp="ku1hh2zgvcd0">
              <ProjectionTable rows={results.rows} inputs={inputs} onInputsChange={setInputs} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={handleDoubleAttritionChange} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} timeframe={timeframe} setTimeframe={setTimeframe} data-id="element-678" data-mp="kt058v6psjtp" />
            </div>

            <div className="mb-6" data-id="element-679" data-mp="3p7gb8mn0d3xi">
              <ProfitChart rows={results.rows} breakeven={results.breakeven} halfSales={halfSales} setHalfSales={setHalfSales} doubleAttrition={doubleAttrition} setDoubleAttrition={handleDoubleAttritionChange} onTarget={onTarget} onToggleOnTarget={handleToggleOnTarget} plusFive={plusFive} onPlusFive={handlePlusFive} data-id="element-680" data-mp="3k9eu47957eoo" />
            </div>

            <div className="flex flex-wrap gap-3 mb-6" data-id="element-681" data-mp="14hj6gyrl59wo">
              <button onClick={handleReset} className="px-3 py-2 rounded border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold text-xs transition-colors active:translate-y-[1px]" data-id="element-682" data-mp="14k5e0yeh7pew">
                Start Over
              </button>
            </div>
          </div>}
      </div>

      {/* Hiring Funnel Modal */}
      <HiringFunnelModal isOpen={showHiringFunnel} onClose={() => setShowHiringFunnel(false)} estimatedHires={reverseHires} data-id="element-683" data-mp="2pvx5v3dyv16c" />
    </div>;
}